// The four canvas direction circles — one consistent set on every page: round
// gold buttons just inside the page's top / bottom / left / right edges (▲ ▼ ◀ ▶).
// A direction with nothing to go to is not drawn; the rest rest dim and light up on hover/press.
import React, { useRef } from 'react';
import { Animated, Pressable, StyleSheet, Text, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { makeStyles, useV10 } from './theme';
import { useTourTarget } from './onboarding/OnboardingProvider';

export const NAV_D = 44; // was 37; +20% (2026-10-12)
const GAP = 6;
type Dir = 'up' | 'down' | 'left' | 'right';
export type EdgeActions = Partial<Record<Dir, (() => void) | undefined>>;

const GLYPH: Record<Dir, string> = { up: '▲', down: '▼', left: '◀', right: '▶' };
const LABEL: Record<Dir, string> = { up: 'Up', down: 'Down', left: 'Left', right: 'Right' };

/** Fills its (positioned) parent; `top`/`bottom` inset the usable page area
 *  (e.g. below the app header). */
/** One circle: dim at rest, lights up to full gold + glow on hover / press. */
function NavCircle({ dir, onPress, place, label, targetId }: { dir: Dir; onPress: () => void; place: object; label: string; targetId?: string }) {
  const t = useV10();
  const tour = useTourTarget(targetId);
  const styles = useStyles();
  const a = useRef(new Animated.Value(0)).current;
  const to = (v: number) => Animated.timing(a, { toValue: v, duration: 150, useNativeDriver: true }).start();
  return (
    <Animated.View style={[styles.tab, place, { opacity: a.interpolate({ inputRange: [0, 1], outputRange: [0.35, 1] }) }]}>
      <Pressable
        ref={tour.onLayoutRef}
        onPress={onPress}
        onHoverIn={() => to(1)}
        onHoverOut={() => to(0)}
        onPressIn={() => to(1)}
        onPressOut={() => to(0)}
        hitSlop={6}
        accessibilityLabel={label}
        style={[StyleSheet.absoluteFill, { borderRadius: NAV_D / 2, overflow: 'hidden' }]}
      >
        <LinearGradient colors={[t.goldBright, t.gold]} style={StyleSheet.absoluteFill} pointerEvents="none" />
        <View style={styles.center} pointerEvents="none">
          <Text style={styles.glyph}>{GLYPH[dir]}</Text>
        </View>
      </Pressable>
      <Animated.View pointerEvents="none" style={[styles.glow, { opacity: a }]} />
    </Animated.View>
  );
}

/** Fills its (positioned) parent; `top`/`bottom` inset the usable page area
 *  (e.g. below the app header). A direction with nothing there is not drawn. */
export function EdgeTabs({ top = 0, bottom = 0, labels, upTargetId, downTargetId, leftTargetId, rightTargetId, ...actions }: { top?: number; bottom?: number; labels?: Partial<Record<Dir, string>>; /** Onboarding tour target ids for the ▲ / ▼ circles. */ upTargetId?: string; downTargetId?: string; /** Tour target ids for the ◀ / ▶ circles. */ leftTargetId?: string; rightTargetId?: string } & EdgeActions) {
  const place = (d: Dir) =>
    d === 'up'
      ? { top: top + GAP, left: '50%', marginLeft: -NAV_D / 2 }
      : d === 'down'
      ? { bottom: bottom + GAP, left: '50%', marginLeft: -NAV_D / 2 }
      : d === 'left'
      ? { left: GAP, top: '50%', marginTop: -NAV_D / 2 + (top - bottom) / 2 }
      : { right: GAP, top: '50%', marginTop: -NAV_D / 2 + (top - bottom) / 2 };
  return (
    <View pointerEvents="box-none" style={[StyleSheet.absoluteFill, { zIndex: 12 }]}>
      {(['up', 'down', 'left', 'right'] as Dir[]).map((d) => {
        const fn = actions[d];
        return fn ? <NavCircle key={d} dir={d} onPress={fn} place={place(d)} label={labels?.[d] ?? LABEL[d]} targetId={d === 'up' ? upTargetId : d === 'down' ? downTargetId : d === 'left' ? leftTargetId : rightTargetId} /> : null;
      })}
    </View>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    tab: { position: 'absolute', width: NAV_D, height: NAV_D, borderRadius: NAV_D / 2, borderWidth: 1, borderColor: t.goldBright, overflow: 'visible', } as object,
    center: { flex: 1, alignItems: 'center', justifyContent: 'center' },
    glow: { ...StyleSheet.absoluteFillObject, borderRadius: NAV_D / 2, boxShadow: `0 0 13px ${t.goldGlow}` } as object,
    glyph: { color: t.onGold, fontSize: 16, fontWeight: '800' },
  }),
);
